import { useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { LoginForm } from '@/features/auth/components/LoginForm';
import { OAuthButtons } from '@/features/auth/components/OAuthButtons';
import { usePaperTheme } from '@/hooks/usePaperTheme';

// oauth_error codes match internal/auth/oauth_handler.go#mapOAuthError.
const OAUTH_ERROR_MESSAGES: Record<string, string> = {
  denied: 'Sign-in was canceled. Try again when ready.',
  unverified: "That provider account's email isn't verified. Verify it and retry.",
  invite_required: 'New accounts are invite-only right now. Sign in with an existing account or request access.',
  state: 'Sign-in link expired. Please try again.',
  disabled: 'That sign-in option is currently disabled.',
  upstream: "Couldn't reach the sign-in provider. Try again in a moment.",
  server: 'Something went wrong. Try again.',
};

export function LoginPage() {
  usePaperTheme();
  const [searchParams, setSearchParams] = useSearchParams();
  const oauthError = searchParams.get('oauth_error');
  // `?reason=expired` is set by the API client when a request 401s on a
  // protected route — it means the user's session ran out, not that they
  // typed bad creds. Surface a clearer toast so they know to just sign back in.
  const expired = searchParams.get('reason') === 'expired';

  useEffect(() => {
    if (!oauthError && !expired) return;
    if (expired) {
      toast('Your session expired — sign in to continue.', { icon: '🔒' });
    } else if (oauthError) {
      toast.error(OAUTH_ERROR_MESSAGES[oauthError] ?? OAUTH_ERROR_MESSAGES.server!);
    }
    const next = new URLSearchParams(searchParams);
    next.delete('oauth_error');
    next.delete('reason');
    setSearchParams(next, { replace: true });
  }, [oauthError, expired, searchParams, setSearchParams]);

  return (
    <div className="min-h-screen bg-paper text-charcoal-soft">
      <nav className="mx-auto flex max-w-6xl items-center justify-between px-6 py-5">
        <Link to="/" className="text-lg font-semibold tracking-tight-ish text-charcoal">
          clyra<span className="text-teal-500">.</span>
        </Link>
        <Link to="/signup" className="text-petit text-charcoal-muted hover:text-charcoal transition-colors">
          Create account
        </Link>
      </nav>

      <main className="mx-auto flex max-w-sm flex-col px-6 pt-12 pb-20">
        <div className="mb-8">
          <h1 className="text-2xl font-semibold tracking-tight-ish text-charcoal">Sign in</h1>
          <p className="mt-1 text-petit text-charcoal-muted">
            Welcome back. Continue where you left off.
          </p>
        </div>

        <div className="rounded-xl border border-stone-subtle bg-paper-raised p-6 shadow-paper-sm">
          <OAuthButtons />
          <div className="mt-4">
            <LoginForm />
          </div>
        </div>

        <div className="mt-6 space-y-2 text-center">
          <Link
            to="/forgot-password"
            className="block text-petit text-charcoal-muted hover:text-charcoal transition-colors"
          >
            Forgot your password?
          </Link>
          <p className="text-petit text-charcoal-muted">
            New here?{' '}
            <Link to="/signup" className="text-teal-600 hover:text-teal-500 transition-colors">
              Create an account
            </Link>
          </p>
        </div>
      </main>
    </div>
  );
}
